import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Typography, TextField, Box, Hidden } from "@mui/material";
import PropTypes from "prop-types";
import LogoSettingsOption from "./LogoSettingsOption";
import { UploadButton } from "../../../components/Buttons";
import { useTranslation } from "react-i18next";

const logoImageMaxSize = { w: 200, h: 60 };
const logoImageMaxSizeText =
  logoImageMaxSize.w + " x " + logoImageMaxSize.h + " px";

const logoSettingsOptions = {
  logoAndText: "logo_and_text",
  logoOnly: "logo",
  textOnly: "text"
};

const useStyles = makeStyles(theme => ({
  subtitle: {
    paddingTop: theme.spacing(2)
  },
  textField: {
    marginBottom: theme.spacing(2.5)
  }
}));

function LogoOption(props) {
  const theme = useTheme();
  const { t } = useTranslation();

  let onLogoUpload = event => {
    if (event.target.files) {
      let file = new Image(),
        rawFile = event.target.files[0];
      file.src = URL.createObjectURL(rawFile);
      file.onload = function() {
        if (
          file.width > logoImageMaxSize.w ||
          file.height > logoImageMaxSize.h
        ) {
          alert(
            t("settings.logoSettings.maxLogoALertMsg", {
              maxSize: logoImageMaxSizeText
            })
          );
        } else {
          props.onLogoUpload(file.src, rawFile);
        }
      };
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" mb={{ xs: 1, sm: 3 }}>
        <Box display="flex" alignItems="center" my={2}>
          <Box
            width={{ xs: 150, sm: 200 }}
            height={{ xs: 45, sm: 60 }}
            mr={3}
            bgcolor={theme.palette.secondary[50]}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {props.logo}
          </Box>
          <UploadButton
            acceptType="image/*"
            buttonId={props.uploadButtonId}
            onUpload={onLogoUpload}
            buttonText={t("settings.logoSettings.uploadBtn")}
          />
        </Box>

        <Typography variant="body2" color="textSecondary">
          {`${logoImageMaxSizeText} max`}
        </Typography>
      </Box>
    </>
  );
}

function TextOption(props) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        maxWidth={{ xs: 248, sm: 428 }}
        mr={2}
        color={theme.palette.text.secondary}
      >
        <TextField
          className={classes.textField}
          value={props.learningCenterCaption}
          onChange={props.onTextInput}
          margin="normal"
          variant="filled"
          onKeyPress={ev => {
            if (ev.key === "Enter") {
              ev.target.blur();
              ev.preventDefault();
            }
          }}
          onBlur={ev => {
            props.onTextInput(ev, true);
            ev.preventDefault();
          }}
          hiddenLabel
        />
        <Box maxWidth={{ xs: 248, sm: 420 }}>
          <Typography variant="body2" color="textSecondary">
            {t("settings.logoSettings.titleMaxLengthText")}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function LogoSettings(props) {
  const { logoStyle, onLogoSettingChanged } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.root}>
      <Box pt={3} pb={2}>
        <LogoSettingsOption
          checked={logoStyle === logoSettingsOptions.logoAndText}
          onLogoSettingChanged={onLogoSettingChanged(
            logoSettingsOptions.logoAndText
          )}
        >
          <Typography variant="h5">
            {t("settings.logoSettings.logoAndText")}
          </Typography>
          <Box
            display="flex"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <LogoOption {...props} uploadButtonId="logo-with-text-upload" />
            <Hidden mdDown>
              <Box position="relative" mx={5} height={56} mb={5}>
                <div className="vertical-divider" />
              </Box>
            </Hidden>
            <TextOption {...props} />
          </Box>
        </LogoSettingsOption>
        <LogoSettingsOption
          checked={logoStyle === logoSettingsOptions.logoOnly}
          onLogoSettingChanged={onLogoSettingChanged(
            logoSettingsOptions.logoOnly
          )}
        >
          <Typography variant="h5">
            {t("settings.logoSettings.logoOnly")}
          </Typography>
          <LogoOption {...props} uploadButtonId="logo-only-upload" />
        </LogoSettingsOption>
        <LogoSettingsOption
          checked={logoStyle === logoSettingsOptions.textOnly}
          onLogoSettingChanged={onLogoSettingChanged(
            logoSettingsOptions.textOnly
          )}
        >
          <Typography variant="h5">
            {t("settings.logoSettings.textOnly")}
          </Typography>
          <TextOption {...props} />
        </LogoSettingsOption>
      </Box>
    </div>
  );
}

export default LogoSettings;

LogoSettingsOption.propTypes = {
  logoStyle: PropTypes.oneOf(Object.keys(logoSettingsOptions)),
  handleChange: PropTypes.func,
  learningCenterCaption: PropTypes.string,
  logo: PropTypes.node,
  onLogoSettingChanged: PropTypes.func
};
